import { useState, useCallback, useEffect } from "react";
import Cropper, { Area } from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Crop, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getLocationCode, getLocationName } from "@/lib/locationCodes";

interface QrCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  qrCodeUrl: string | null;
  packageId: string;
  retailer: string;
  pickupDate: string | null; // YYYY-MM-DD
  sequenceNumber: number;
  onComplete: (croppedQrUrl: string) => void;
}

/**
 * Creates an image element from a URL, returning a promise.
 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Crops an image given pixel crop area, then composites with a label bar below.
 * Returns a Blob of the final PNG.
 */
async function generateLabeledQr(
  imageSrc: string,
  cropPixels: Area,
  verificationCode: string,
  locationName: string
): Promise<Blob> {
  const img = await loadImage(imageSrc);

  const cropSize = Math.max(cropPixels.width, cropPixels.height);
  const labelHeight = Math.round(cropSize * 0.3);
  const totalHeight = cropSize + labelHeight;

  const canvas = document.createElement("canvas");
  canvas.width = cropSize;
  canvas.height = totalHeight;
  const ctx = canvas.getContext("2d")!;

  // White background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, cropSize, totalHeight);

  // Draw cropped QR
  ctx.drawImage(
    img,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    cropSize,
    cropSize
  );

  // Label bar background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, cropSize, cropSize, labelHeight);

  // Draw border line between QR and label
  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, cropSize);
  ctx.lineTo(cropSize, cropSize);
  ctx.stroke();

  // Verification code (bold, larger)
  const codeFontSize = Math.round(labelHeight * 0.42);
  ctx.fillStyle = "#000000";
  ctx.font = `bold ${codeFontSize}px Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(verificationCode, cropSize / 2, cropSize + labelHeight * 0.35);

  // Location name (smaller, below code)
  const nameFontSize = Math.round(labelHeight * 0.28);
  ctx.fillStyle = "#6b7280";
  ctx.font = `${nameFontSize}px Arial, sans-serif`;
  ctx.fillText(locationName, cropSize / 2, cropSize + labelHeight * 0.75);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Failed to generate image"));
    }, "image/png");
  });
}

export const QrCropDialog = ({
  open,
  onOpenChange,
  qrCodeUrl,
  packageId,
  retailer,
  pickupDate,
  sequenceNumber,
  onComplete,
}: QrCropDialogProps) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewBlob, setPreviewBlob] = useState<string | null>(null);

  // Generate the verification code
  const locationCode = getLocationCode(retailer);
  const locationName = getLocationName(locationCode);
  
  const datePrefix = pickupDate
    ? `${pickupDate.substring(5, 7)}-${pickupDate.substring(8, 10)}`
    : "00-00";
  
  const verificationCode = `${datePrefix}-${locationCode}-${String(sequenceNumber).padStart(3, "0")}`;

  // Load signed URL when dialog opens
  useEffect(() => {
    if (!open || !qrCodeUrl) return;
    
    const loadSignedUrl = async () => {
      setLoadingUrl(true);
      try {
        // Extract file path
        let filePath = qrCodeUrl;
        if (qrCodeUrl.startsWith("http")) {
          const url = new URL(qrCodeUrl);
          const match = url.pathname.match(/\/storage\/v1\/object\/sign\/[^/]+\/(.+)/);
          if (match) filePath = decodeURIComponent(match[1]);
        }

        const { data, error } = await supabase.storage
          .from("qr-codes")
          .createSignedUrl(filePath, 3600);
        
        if (error) throw error;
        setSignedUrl(data.signedUrl);
      } catch (err) {
        console.error("Failed to load QR image:", err);
        toast.error("Failed to load QR code image");
      } finally {
        setLoadingUrl(false);
      }
    };

    loadSignedUrl();
    // Reset state
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setPreviewBlob(null);
  }, [open, qrCodeUrl]);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handlePreview = async () => {
    if (!signedUrl || !croppedAreaPixels) return;
    try {
      const blob = await generateLabeledQr(signedUrl, croppedAreaPixels, verificationCode, locationName);
      const url = URL.createObjectURL(blob);
      setPreviewBlob(url);
    } catch (err) {
      console.error("Preview failed:", err);
      toast.error("Failed to generate preview");
    }
  };

  const handleSave = async () => {
    if (!signedUrl || !croppedAreaPixels) return;

    setSaving(true);
    try {
      const blob = await generateLabeledQr(signedUrl, croppedAreaPixels, verificationCode, locationName);
      
      const fileName = `${packageId}/${verificationCode}.png`;
      
      const { error: uploadError } = await supabase.storage
        .from("qr-labels")
        .upload(fileName, blob, { contentType: "image/png", upsert: true });
      
      if (uploadError) throw uploadError;

      // Update the package with the cropped QR URL
      const { error: updateError } = await (supabase as any)
        .from("packages")
        .update({ cropped_qr_url: fileName })
        .eq("id", packageId);
      
      if (updateError) throw updateError;

      toast.success("QR label saved successfully");
      onComplete(fileName);
      onOpenChange(false);
    } catch (err: any) {
      console.error("Failed to save QR label:", err);
      toast.error(err.message || "Failed to save QR label");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crop className="h-5 w-5" />
            Crop QR Code
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Verification code preview */}
          <div className="text-center p-3 bg-muted rounded-lg border">
            <p className="text-xs text-muted-foreground mb-1">Verification Code</p>
            <p className="text-lg font-bold font-mono">{verificationCode}</p>
            <p className="text-sm text-muted-foreground">{locationName}</p>
          </div>

          {loadingUrl ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : previewBlob ? (
            /* Preview mode */
            <div className="space-y-3">
              <div className="border rounded-lg overflow-hidden bg-white p-4 flex items-center justify-center">
                <img
                  src={previewBlob}
                  alt="QR Label Preview"
                  className="max-w-full max-h-[400px] object-contain"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setPreviewBlob(null)}
                >
                  Re-crop
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Save Label
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : signedUrl ? (
            /* Crop mode */
            <div className="space-y-3">
              <div className="relative h-[350px] bg-muted rounded-lg overflow-hidden">
                <Cropper
                  image={signedUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Zoom</span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handlePreview}
                  disabled={!croppedAreaPixels}
                >
                  <Crop className="h-4 w-4 mr-2" />
                  Preview Label
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No QR code image available to crop.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
